/**
 * Recipes: a dish made from foods. Its nutrition is the sum of its ingredients
 * (each one's per-100 g values times its grams), and it behaves like a food
 * everywhere (search, favorites, Recent, logging) through its ref, "recipe:<uuid>".
 * Shared so the editor's live numbers use the same math as the server. Kept free
 * of zod so the web bundle doesn't pull in the validation library.
 */
import type { FoodView } from './api';
import type { Serving } from './foods';
import { MAX_NUTRIENT_KEYS, perServingToPer100, sumWithCoverage, scaleMap, type NutrientMap } from './nutrients';

export const RECIPE_PREFIX = 'recipe:';

/** Bounds for one person's recipes. The server enforces them (shared/api.ts) and the editor follows them. */
export const RECIPE_LIMITS = {
  /** Recipes one person can have. Deleted ones don't count. */
  recipes: 500,
  ingredients: 60,
  servings: { min: 1, max: 100 },
  name: 120,
  notes: 10_000,
  /** Nutrients in one ingredient, and different nutrients across a whole recipe. */
  nutrients: MAX_NUTRIENT_KEYS,
  /** The finished dish, in grams. */
  cookedGrams: 50_000,
  sourceUrl: 2_000,
} as const;

/** One log entry's limit, and one ingredient's (gramsSchema in shared/api.ts, GramsInput's top). A heavier serving isn't offered as a preset. */
export const MAX_ENTRY_GRAMS = 5000;

/** What the math needs from an ingredient. */
export interface IngredientAmount {
  grams: number;
  per100: NutrientMap;
}

export interface RecipeNutrition {
  /** The whole dish: every ingredient's per-100 g values times its grams, added up. */
  total: NutrientMap;
  /** How many ingredients had a value for each nutrient. Fewer than `count` means that total is partial (at least). */
  known: Record<string, number>;
  count: number;
  /** The raw ingredients' weight. */
  rawGrams: number;
  /** What the dish weighs: the cooked weight, or the raw ingredients' weight without one. */
  dishGrams: number;
  /** One serving's weight: the dish over its servings. */
  servingGrams: number;
  perServing: NutrientMap;
  /** Per 100 g of the dish: what logging a gram of it counts. */
  per100: NutrientMap;
}

/** A recipe's stored nutrition when there's none to read (it always has some once saved). */
export function noNutrition(): RecipeNutrition {
  return { total: {}, known: {}, count: 0, rawGrams: 0, dishGrams: 0, servingGrams: 0, perServing: {}, per100: {} };
}

function divide(map: NutrientMap, by: number): NutrientMap {
  const out: NutrientMap = {};
  for (const [key, value] of Object.entries(map)) out[key] = value / by;
  return out;
}

/**
 * Nutrition for a recipe. Per serving is the total over the servings. Per 100 g is
 * the total over the cooked weight, or over the raw ingredients' weight when
 * there's no cooked weight (then grams of the dish count as raw ingredients).
 */
export function recipeNutrition(ingredients: readonly IngredientAmount[], servings: number, cookedGrams: number | null = null): RecipeNutrition {
  const { totals, known, count } = sumWithCoverage(ingredients.map((i) => scaleMap(i.per100, i.grams)));
  const rawGrams = ingredients.reduce((sum, i) => sum + i.grams, 0);
  const dishGrams = cookedGrams !== null && cookedGrams > 0 ? cookedGrams : rawGrams;
  const portions = servings > 0 ? servings : 1;
  return {
    total: totals,
    known,
    count,
    rawGrams,
    dishGrams,
    servingGrams: dishGrams / portions,
    perServing: divide(totals, portions),
    per100: perServingToPer100(totals, dishGrams),
  };
}

const roundGrams = (g: number) => Math.round(g * 10) / 10;

function gramsLabel(grams: number): string {
  return grams >= 10 ? Math.round(grams).toLocaleString('en-US') : String(grams);
}

/**
 * The amounts offered when logging a recipe: "1 serving (362 g)", then "100 g".
 * A serving too light to round to 0.1 g, or heavier than one entry can be, isn't offered.
 */
export function recipeServings(servingGrams: number): Serving[] {
  const grams = roundGrams(servingGrams);
  const out: Serving[] = [];
  if (grams >= 0.1 && grams <= MAX_ENTRY_GRAMS) out.push({ label: `1 serving (${gramsLabel(grams)} g)`, grams });
  if (grams !== 100) out.push({ label: '100 g', grams: 100 });
  return out;
}

/**
 * A recipe as a food: what search, favorites, Recent and logging see. A log entry keeps
 * only per-100 g values, so once logged, a nutrient that only some ingredients had (fiber
 * for 4 of 5, say) counts as known in day totals: the recipe page shows that coverage, the
 * log can't.
 */
export function recipeAsFood(recipe: { id: string; name: string }, nutrition: RecipeNutrition): FoodView {
  return {
    ref: RECIPE_PREFIX + recipe.id,
    name: recipe.name,
    brand: null,
    per100: nutrition.per100,
    servings: recipeServings(nutrition.servingGrams),
    kind: 'recipe',
    liquid: false,
    barcode: null,
  };
}

/** Links are kept and shown only when they're http(s) without a user name or password. Returns the normalized link. */
export function safeHttpUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}
